import { ConnectedDevice, DeviceAvailableForLaunch, DeviceScreen, FormattedFlow, MockEvent, Repl, ReplCommand } from './models';
import useSWR, { mutate, SWRConfiguration, SWRResponse } from 'swr';
import { getHost } from './util/host';

export type ReplResponse = {
  repl?: Repl
  error?: any
}

export type DevicesResponse = {
  data?: {
    connected: ConnectedDevice[],
    availableForLaunch: DeviceAvailableForLaunch[]
  },
  error?: any,
}

type GetMockDataResponse = {
  projectId: string,
  events: MockEvent[]
}

export class HttpError extends Error {

  constructor(
    public status: number,
    public message: string,
  ) {
    super(message);
  }
}

export const wait = async (durationMs: number) => {
  return new Promise(resolve => setTimeout(resolve, durationMs))
}

const makeRequest = async <T>(method: string, path: string, body?: Object | undefined): Promise<T> => {
  const response = await fetch(path, {
    method,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
    body: body ? JSON.stringify(body) : undefined,
  })
  if (!response.ok) {
    const body = await response.text()
    throw new HttpError(response.status, body)
  }
  return await response.json()
}

const useRepl = (): ReplResponse => {
  const {data: repl, error} = useSWR<Repl>('/api/repl/watch')
  return { repl, error }
}

const fetcher = (url: string) => fetch(`${getHost()}${url}`).then(r => r.json())

export const API = {
  useDeviceScreen: (config?: SWRConfiguration<DeviceScreen>): SWRResponse<DeviceScreen> => {
    return useSWR('/api/device-screen', (url) => makeRequest('GET', `${getHost()}${url}`), config)
  },
  getDeviceScreen: async (): Promise<DeviceScreen> => {
    return makeRequest('GET', `${getHost()}/api/device-screen`)
  },
  repl: {
    useRepl,
    runCommand: async (yaml: string): Promise<Repl> => {
      return makeRequest('POST', `${getHost()}/api/repl/command`, { yaml })
    },
    runCommandsById: async (ids: string[]): Promise<Repl> => {
      return makeRequest('POST', `${getHost()}/api/repl/command`, { ids })
    },
    deleteCommands: async (ids: string[]): Promise<Repl> => {
      return makeRequest('DELETE', `${getHost()}/api/repl/command`, { ids })
    },
    reorderCommands: async (ids: string[]) => {
      makeRequest('POST', `${getHost()}/api/repl/command/reorder`, { ids })
      mutate<Repl>('/api/repl/watch', (repl) => {
        if (!repl) return undefined
        const newCommands: ReplCommand[] = []
       const idsAsSet = new Set(ids)
       repl.commands.filter(c => !!c && idsAsSet.has(c.id)).forEach(c => newCommands.push(c))
        return {...repl, commands: newCommands}
      }, {
        revalidate: false
      })
    },
    formatFlow: async (ids: string[]): Promise<FormattedFlow> => {
      return makeRequest('POST', `${getHost()}/api/repl/command/format`, { ids })
    },
  },
  useMockData: (config?: SWRConfiguration) => {
    return useSWR<GetMockDataResponse>('/api/mock-server/data', fetcher, config)
  },
  device: {
    listDevices: async (): Promise<{ connected: ConnectedDevice[], availableForLaunch: DeviceAvailableForLaunch[] }> => {
      return makeRequest('GET', `${getHost()}/api/devices`)
    },
    getSelectedDevice: async (): Promise<ConnectedDevice | null> => {
      return makeRequest('GET', `${getHost()}/api/devices/selected`)
    },
    useSelectedDevice: (config?: SWRConfiguration<ConnectedDevice>): SWRResponse<ConnectedDevice> => {
      return useSWR('/api/devices/selected', (url) => makeRequest('GET', `${getHost()}${url}`), config)
    },
    selectDevice: async(instanceId?: string, modelId?: string): Promise<ConnectedDevice> => {
      return makeRequest('POST', `${getHost()}/api/devices/select`, { instanceId, modelId })
    },
  }
}

const startReplLongPoll = async () => {
  let replVersion = -1
  while (true) {
    try {
      const repl: Repl = await makeRequest('GET', `${getHost()}/api/repl/watch?currentVersion=${replVersion}`)
      mutate('/api/repl/watch', repl, { revalidate: false })
      replVersion = repl.version
    } catch (e) {
      await wait(1000)
    }
  }
}

startReplLongPoll()
